import { api } from "./api.ts";
import type {
  ActiveOverride,
  Content,
  ContentType,
  Display,
  EntryInput,
  MachineCommand,
  OverrideMessage,
  Playlist,
  Schedule,
  ScheduleDetail,
  ScheduleEntry,
} from "./types.ts";

// ---- Content ----

/** The working pool. `archived` fetches what has been taken down instead. */
export const listContent = (archived = false) =>
  api.get<Content[]>(archived ? "/content?archived=1" : "/content");
export const getContent = (id: string) => api.get<Content>(`/content/${id}`);
export const createContent = (body: {
  name: string;
  type: ContentType;
  url?: string | null;
  data?: Record<string, unknown> | null;
}) => api.post<Content>("/content", body);
export const updateContent = (
  id: string,
  body: {
    name?: string;
    url?: string | null;
    data?: Record<string, unknown> | null;
    /** "YYYY-MM-DD" (taken down at the end of that day) or null to run on. */
    expiresAt?: string | null;
    archived?: boolean;
  },
) => api.patch<Content>(`/content/${id}`, body);
export const deleteContent = (id: string) => api.del<void>(`/content/${id}`);

/**
 * Upload a file to Blob storage via a short-lived SAS, returning the canonical
 * (unsigned) blob URL to persist on a content row.
 * Flow: ask the API for an upload URL → PUT the bytes to it → return blobUrl.
 */
export async function uploadFile(file: File): Promise<string> {
  const { uploadUrl, blobUrl } = await api.post<{
    uploadUrl: string;
    blobUrl: string;
  }>("/content/upload-url", {
    filename: file.name,
    contentType: file.type || "application/octet-stream",
  });

  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
  return blobUrl;
}

// ---- Playlists ----

export interface PlaylistDetail extends Playlist {
  /** An import owns this playlist: it rebuilds its own slides on every sync. */
  sourceOwned?: boolean;
  items: Array<{
    contentId: string;
    durationSeconds: number;
    position: number;
    name: string;
    type: ContentType;
    url: string | null;
    data: Record<string, unknown> | null;
    /** Came from the import, so the next sync will rebuild it. */
    imported?: boolean;
  }>;
}

export const listPlaylists = () => api.get<Playlist[]>("/playlists");
export const getPlaylist = (id: string) =>
  api.get<PlaylistDetail>(`/playlists/${id}`);
export const createPlaylist = (name: string) =>
  api.post<Playlist>("/playlists", { name });
export const renamePlaylist = (id: string, name: string) =>
  api.patch<Playlist>(`/playlists/${id}`, { name });
export const deletePlaylist = (id: string) => api.del<void>(`/playlists/${id}`);
/** Append one slide, leaving the existing order untouched. Use this rather than
 *  setPlaylistItems to add: that one replaces the list and renumbers it, which
 *  drags the auto sermon slides out of the position band that keeps them clear
 *  of the PowerPoint sync. */
export const addPlaylistItem = (
  id: string,
  contentId: string,
  durationSeconds?: number,
) => api.post<void>(`/playlists/${id}/items`, { contentId, durationSeconds });

export const setPlaylistItems = (
  id: string,
  items: Array<{ contentId: string; durationSeconds: number }>,
) => api.put<void>(`/playlists/${id}/items`, items);

// ---- Emergency override ----

export const listOverrideMessages = () =>
  api.get<OverrideMessage[]>("/override/messages");
export const createOverrideMessage = (name: string, contentId: string) =>
  api.post<OverrideMessage>("/override/messages", { name, contentId });
export const deleteOverrideMessage = (id: string) =>
  api.del<void>(`/override/messages/${id}`);
export const seedOverrideMessages = () =>
  api.post<{ created: number }>("/override/seed");
export const getActiveOverrides = () =>
  api.get<ActiveOverride[]>("/override/active");
export const activateOverride = (body: {
  contentId: string;
  scope: "all" | "subset";
  displayIds?: string[];
  /** Minutes from now, or omit/null to run until someone clears it. Sent as a
   *  duration, not a deadline — the server dates it off the database clock, so
   *  a phone with a wrong clock can't post an already-expired override. */
  durationMinutes?: number | null;
}) =>
  api.post<{ id: string; expiresAt: string | null }>(
    "/override/activate",
    body,
  );
export const clearOverride = (id?: string) =>
  api.post<{ ok: true }>("/override/clear", id ? { id } : {});

// ---- AI composer ----

/** A flyer the volunteer attached, base64 with no `data:` prefix. Images are
 *  downscaled client-side (see AIComposer); PDFs go up as-is. */
export interface ComposeAttachment {
  mediaType: string;
  data: string;
  name?: string;
}

export interface ComposeMessage {
  role: "user" | "assistant";
  content: string;
  /** User turns only. */
  attachments?: ComposeAttachment[];
}

/** `qrUrl` is encoded server-side and handed to the model as a finished SVG
 *  group — the model places it but never draws it. Sent on every turn, so a
 *  revision keeps the code. */
export const composeSlide = (
  messages: ComposeMessage[],
  orientation: "landscape" | "portrait",
  qrUrl?: string | null,
) =>
  api.post<{ svg: string }>("/compose-slide", {
    messages,
    orientation,
    ...(qrUrl ? { qrUrl } : {}),
  });

/** One problem the slide check found. `fix` may be empty. */
export interface SlideFinding {
  severity: "high" | "medium" | "low";
  what: string;
  fix: string;
}

/** Proofread a finished slide before it goes on a wall. Counts against the same
 *  daily AI cap as composing. */
export const reviewSlide = (
  svg: string,
  orientation: "landscape" | "portrait",
) => api.post<{ findings: SlideFinding[] }>("/review-slide", { svg, orientation });

/** Alt text and a short label for an image, written onto its content record.
 *  Counts against the same daily AI cap. */
export const describeImage = (contentId: string) =>
  api.post<{ alt: string; summary: string }>("/describe-image", { contentId });

/** What `describe-image` leaves on an image's `data`. */
export interface ImageDescription {
  alt?: string;
  summary?: string;
}

// ---- Content sources (PowerPoint / Canva import) ----

export interface Source {
  id: string;
  kind: "powerpoint" | "canva";
  name: string;
  externalRef: Record<string, unknown>;
  playlistId: string | null;
  syncMode: string;
  syncIntervalMinutes: number | null;
  slideDurationSeconds: number;
  lastSyncedAt: string | null;
  lastVersion: string | null;
  realtime: boolean;
  subscriptionExpiresAt: string | null;
  isSiteDefault: boolean;
  status: string;
  lastError: string | null;
  createdAt: string;
}

export const listSources = () => api.get<Source[]>("/sources");
export const createSource = (body: {
  kind: "powerpoint";
  name: string;
  siteId?: string;
  folderPath?: string;
  slideDurationSeconds?: number;
}) => api.post<Source>("/sources", body);
export const createCanvaSource = (body: {
  name: string;
  designId: string;
  slideDurationSeconds?: number;
}) => api.post<Source>("/sources", { kind: "canva", ...body });
export const syncSource = (id: string) =>
  api.post<{ slides: number; deck: string; carried?: number; skipped?: string }>(
    `/sources/${id}/sync`,
  );
export const setSourceRealtime = (id: string, enabled: boolean) =>
  api.post<Source>(`/sources/${id}/realtime`, { enabled });
export const setSourceScheduled = (id: string, enabled: boolean) =>
  api.post<Source>(`/sources/${id}/scheduled`, { enabled });
export const setSourceSiteDefault = (id: string, enabled: boolean) =>
  api.post<Source>(`/sources/${id}/set-default`, { enabled });
/** Retimes the source AND the slides it has already imported. */
export const setSourceSlideSeconds = (id: string, slideDurationSeconds: number) =>
  api.patch<Source>(`/sources/${id}`, { slideDurationSeconds });
export const deleteSource = (id: string) => api.del<void>(`/sources/${id}`);

// ---- Canva connection ----
export const canvaStatus = () => api.get<{ connected: boolean }>("/canva/status");
export const disconnectCanva = () => api.post<{ ok: true }>("/canva/disconnect");
/** OAuth start is a full-page redirect (Canva bounces back to the callback). */
export const canvaConnectUrl = "/api/canva/oauth/start";

// ---- Schedules ----

export const listSchedules = () => api.get<Schedule[]>("/schedules");
export const getSchedule = (id: string) =>
  api.get<ScheduleDetail>(`/schedules/${id}`);
export const createSchedule = (name: string) =>
  api.post<Schedule>("/schedules", { name });
export const renameSchedule = (id: string, name: string) =>
  api.patch<Schedule>(`/schedules/${id}`, { name });
export const deleteSchedule = (id: string) => api.del<void>(`/schedules/${id}`);
export const addEntry = (scheduleId: string, entry: EntryInput) =>
  api.post<ScheduleEntry>(`/schedules/${scheduleId}/entries`, entry);
export const updateEntry = (
  scheduleId: string,
  entryId: string,
  entry: EntryInput,
) => api.patch<ScheduleEntry>(`/schedules/${scheduleId}/entries/${entryId}`, entry);
export const deleteEntry = (scheduleId: string, entryId: string) =>
  api.del<void>(`/schedules/${scheduleId}/entries/${entryId}`);

// ---- Displays ----

export const listDisplays = () => api.get<Display[]>("/displays");
export const getDisplay = (id: string) => api.get<Display>(`/displays/${id}`);
export const createDisplay = (name: string, orientation: "landscape" | "portrait") =>
  api.post<Display>("/displays", { name, orientation });
export const updateDisplay = (
  id: string,
  body: {
    name?: string;
    scheduleId?: string | null;
    defaultPlaylistId?: string | null;
    orientation?: "landscape" | "portrait";
  },
) => api.patch<Display>(`/displays/${id}`, body);
export const deleteDisplay = (id: string) => api.del<void>(`/displays/${id}`);
export const rotateDisplayToken = (id: string) =>
  api.post<Display>(`/displays/${id}/rotate-token`);

export type DisplayCommand = "reload" | "refresh" | "blank" | "unblank";
export const sendDisplayCommand = (id: string, action: DisplayCommand) =>
  api.post<{ ok: true }>(`/displays/${id}/command`, { action });
export const broadcastCommand = (action: DisplayCommand) =>
  api.post<{ ok: true }>(`/realtime/broadcast`, { action });

// Machine-level actions handled by the NUC agent (queued, pulled on next check-in).
export const sendMachineCommand = (
  id: string,
  kind: MachineCommand,
  payload?: Record<string, unknown>,
) => api.post<{ ok: true; id: string }>(`/displays/${id}/machine-command`, {
  kind,
  ...(payload ? { payload } : {}),
});
